import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

interface Props {
  /** Show the right-hand operations rail skeleton (desktop labs). Default true. */
  withRightRail?: boolean;
}

/**
 * Shell-shaped loading state shown while `useStudio` resolves — a left swatch
 * rail, a center canvas block, and an optional right operations rail rendered
 * as shimmering skeletons. Replaces the bare centered spinner so the layout
 * doesn't flash empty then snap into place.
 */
export const LabLoadingSkeleton: React.FC<Props> = ({ withRightRail = true }) => (
  <Box
    data-testid="lab-loading-skeleton"
    aria-busy
    aria-label="Loading studio"
    sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}
  >
    {/* TopBar stand-in */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        minHeight: 48,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Skeleton variant="circular" width={20} height={20} />
      <Skeleton variant="text" width={120} />
      <Skeleton variant="rounded" width={140} height={24} sx={{ ml: 1 }} />
    </Box>

    <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* Swatch rail */}
      <Box sx={{ width: 240, flexShrink: 0, p: 2, borderRight: theme => `1px solid ${theme.palette.divider}` }}>
        <Skeleton variant="text" width={80} sx={{ mb: 1.5 }} />
        {[0, 1, 2].map(group => (
          <Box key={group} sx={{ mb: 2.5 }}>
            <Skeleton variant="text" width={110} sx={{ mb: 1 }} />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" width={56} height={56} />
              ))}
            </Stack>
          </Box>
        ))}
      </Box>

      {/* Canvas */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Skeleton variant="rounded" sx={{ width: '100%', height: '100%', maxWidth: 720, maxHeight: 520 }} />
      </Box>

      {/* Operations rail */}
      {withRightRail && (
        <Box sx={{ width: 300, flexShrink: 0, p: 2, borderLeft: theme => `1px solid ${theme.palette.divider}` }}>
          <Skeleton variant="text" width={140} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={56} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={120} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={40} width={160} />
        </Box>
      )}
    </Box>
  </Box>
);
